import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_ROLES } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';

interface Member {
  id: string;
  userId: string;
  role: string;
  status: string;
  email: string;
  fullName: string;
  persona: string;
  joinedAt: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  code: string;
  expiresAt: string;
  acceptedAt: string | null;
}

/** People, roles and invitations. The school administrator's main screen. */
export function MembersPage() {
  const queryClient = useQueryClient();
  const { permissions, activeOrg } = useSession();
  const has = (permission: string) =>
    permissions.includes('*@*') || permissions.includes(permission);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('TEACHER');
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => apiClient.get<Member[]>('/v1/org/members'),
  });

  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: () => apiClient.get<Invitation[]>('/v1/org/invitations'),
    enabled: has('member@invite'),
  });

  const invite = useMutation({
    mutationFn: () => apiClient.post<Invitation>('/v1/org/invitations', { email, role }),
    onSuccess: () => {
      setEmail('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: caught => setError((caught as Error).message),
  });

  const assign = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      apiClient.patch(`/v1/org/members/${id}/role`, { role: next }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
    onError: caught => setError((caught as Error).message),
  });

  const suspend = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/org/members/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
    onError: caught => setError((caught as Error).message),
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">People at {activeOrg?.name}</h1>
      <p className="text-sm text-ink-600">
        Roles are granted here, and they decide what somebody can do. What they can <em>reach</em> is
        a separate question, answered by the subscription.
      </p>

      {error && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      {has('member@invite') && (
        <section className="card mt-5">
          <h2 className="text-sm font-semibold text-ink-900">Invite someone</h2>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div className="min-w-[16rem] flex-1">
              <label className="label" htmlFor="invite-email">
                Email
              </label>
              <input
                id="invite-email"
                className="input"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="invite-role">
                Role
              </label>
              <select
                id="invite-role"
                className="input"
                value={role}
                onChange={event => setRole(event.target.value)}
              >
                {DEFAULT_ROLES.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={!email || invite.isPending}
              onClick={() => invite.mutate()}
            >
              Send invitation
            </button>
          </div>

          {invitations.data && invitations.data.filter(row => !row.acceptedAt).length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
                Pending invitations
              </p>
              <ul className="mt-1 divide-y divide-ink-100 text-sm">
                {invitations.data
                  .filter(row => !row.acceptedAt)
                  .map(row => (
                    <li key={row.id} className="flex flex-wrap items-center gap-2 py-2">
                      <span className="min-w-0 flex-1 truncate">{row.email}</span>
                      <span className="badge bg-ink-200 text-ink-700">{row.role}</span>
                      {/*
                        The code is shown, not just emailed. Email delivery to a
                        school domain in Kenya is not reliable, and a head
                        teacher reading a code down the phone is a real workflow.
                      */}
                      <code className="rounded bg-ink-100 px-2 py-0.5 text-xs">{row.code}</code>
                      <button
                        type="button"
                        className="text-xs text-brand-700 underline"
                        onClick={() =>
                          void navigator.clipboard?.writeText(
                            `${window.location.origin}/onboarding?code=${row.code}`,
                          )
                        }
                      >
                        Copy link
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <section className="card mt-4 overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Status</th>
              {has('member@remove') && <th className="px-4 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(members.data ?? []).map(member => (
              <tr key={member.id}>
                <td className="px-4 py-2">{member.fullName}</td>
                <td className="px-4 py-2 text-ink-600">{member.email}</td>
                <td className="px-4 py-2">
                  {has('member@assign_role') ? (
                    <select
                      className="input py-1 text-xs"
                      value={member.role}
                      onChange={event => assign.mutate({ id: member.id, next: event.target.value })}
                    >
                      {DEFAULT_ROLES.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="badge bg-ink-200 text-ink-700">{member.role}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs capitalize text-ink-600">{member.status}</td>
                {has('member@remove') && (
                  <td className="px-4 py-2 text-right">
                    {member.status === 'active' && (
                      <button
                        type="button"
                        className="text-xs text-red-700 underline"
                        onClick={() => suspend.mutate(member.id)}
                      >
                        Suspend
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className="mt-3 text-xs text-ink-500">
        Suspending keeps a person's enrolments, laboratory runs and certificates — those are the
        school's records, not the individual's — and stops them signing in to this organisation.
      </p>
    </div>
  );
}
